import Contract from '../contract';
import ISav from '../sav';

/**
 * Récupère une valeur depuis les formulaires d'un contrat
 * @param contract Objet contrat
 * @param key Clé du champ à récupérer
 * @returns La valeur associée à la clé ou null si introuvable
 */
export function getContractFormValue(contract: Contract | null | undefined, key: string): string | number | boolean | null {
  if (!contract?.valFormulaires) {
    return null;
  }

  const valFormulaire = contract.valFormulaires.find((valForm) => valForm.champFormulaire.key === key);
  if (!valFormulaire) {
    return null;
  }

  switch (valFormulaire.champFormulaire.type.toLowerCase()) {
    case 'text':
      return valFormulaire.valText;
    case 'textarea':
      return valFormulaire.valTextarea;
    case 'date':
      return valFormulaire.valDate;
    case 'checkbox':
      return valFormulaire.valCheckbox;
    case 'int':
      return valFormulaire.valInt;
    case 'double':
      return valFormulaire.valDouble;
    default:
      return null;
  }
}

/**
 * Récupère une valeur depuis les formulaires d'un SAV
 * @param sav Objet SAV
 * @param key Clé du champ à récupérer
 * @returns La valeur associée à la clé ou null si introuvable
 */
export function getSavFormValue(sav: ISav | null | undefined, key: string): string | number | boolean | null {
  if (!sav?.savFormValues) {
    return null;
  }

  const formValue = sav.savFormValues.find(value => value.formField?.key === key);
  if (!formValue) {
    return null;
  }

  // Selon le type de champ, utiliser la propriété appropriée
  if (formValue.valueText) return formValue.valueText;
  if (formValue.valueInt !== undefined && formValue.valueInt !== null) return formValue.valueInt;
  if (formValue.valueDate) return formValue.valueDate;
  if (formValue.valueBoolean !== undefined) return formValue.valueBoolean;
  if (formValue.value) return formValue.value;

  return null;
}

/**
 * Récupère le kilométrage actuel d'un SAV
 * @param sav Objet SAV
 * @returns Le kilométrage actuel sous forme de nombre ou null si introuvable
 */
export function getCurrentKm(sav: ISav | null | undefined): number | null {
  const kmValue = getSavFormValue(sav, 'current_km');
  if (!kmValue) {
    return null;
  }

  const kmNumber = parseInt(kmValue.toString(), 10);
  return isNaN(kmNumber) ? null : kmNumber;
}

/**
 * Récupère le kilométrage initial du contrat d'un SAV
 * @param sav Objet SAV
 * @returns Le kilométrage initial sous forme de nombre ou null si introuvable
 */
export function getInitialKm(sav: ISav | null | undefined): number | null {
  if (!sav?.contract) {
    return null;
  }

  const kmStart = getContractFormValue(sav.contract, 'km_start');
  if (!kmStart) {
    return null;
  }

  const kmNumber = parseInt(kmStart.toString(), 10);
  return isNaN(kmNumber) ? null : kmNumber;
}

/**
 * Récupère la marque du véhicule
 * @param contract Objet contrat
 * @returns La marque du véhicule ou null si introuvable
 */
export function getVehicleBrand(contract: Contract | null | undefined): string | null {
  const brand = getContractFormValue(contract, 'brand');
  return brand ? brand.toString() : null;
}

/**
 * Récupère le modèle du véhicule
 * @param contract Objet contrat
 * @returns Le modèle du véhicule ou null si introuvable
 */
export function getVehicleModel(contract: Contract | null | undefined): string | null {
  const model = getContractFormValue(contract, 'model');
  return model ? model.toString() : null;
}

/**
 * Récupère l'immatriculation du véhicule
 * @param contract Objet contrat
 * @returns L'immatriculation du véhicule ou null si introuvable
 */
export function getVehicleRegistration(contract: Contract | null | undefined): string | null {
  const registration = getContractFormValue(contract, 'registration');
  return registration ? registration.toString() : null;
}

/**
 * Récupère le nom complet du client
 * @param contract Objet contrat
 * @returns Le nom complet du client ou null si introuvable
 */
export function getCustomerName(contract: Contract | null | undefined): string | null {
  if (!contract?.beneficiary) {
    return null;
  }

  const firstName = contract.beneficiary.firstName || '';
  const lastName = contract.beneficiary.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || null;
}

/**
 * Récupère les informations principales du véhicule
 * @param contract Objet contrat
 * @returns Un objet contenant les informations du véhicule
 */
export function getVehicleInfo(contract: Contract | null | undefined) {
  return {
    brand: getVehicleBrand(contract),
    model: getVehicleModel(contract),
    registration: getVehicleRegistration(contract),
    fiscalPower: getContractFormValue(contract, 'fiscal_power'),
    entryIntoService: getContractFormValue(contract, 'entry_into_service'),
    kmStart: getContractFormValue(contract, 'km_start'),
  };
}